import { Collapse, Form, Input, Select, Tooltip } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { ReactComponent as Reset } from 'assets/images/icons/reset.min.svg';
import { AntButton } from 'components/ant/AntButton/AntButton';
import { AntCol } from 'components/ant/AntCol';
import { AntRow } from 'components/ant/AntRow';
import { Modal } from 'components/UI';
import { Label } from 'components/UI/Typography/styled';
import { localeOptions } from 'components/User/Profile/Details/consts';
import React from 'react';
import { TranslateRequest } from 'utils/hooks/useAiTranslator';

import s from './AiWriterModal.module.less';
import { AiWriterModalFormFieldEnum } from './AiWriterModal.types';
import { useAiWriterModal } from './useAiWriterModal';

export const AiWriterModal: React.FC = () => {
  const {
    form,
    modalVisible,
    isUserEdited,
    isImprovedEmpty,
    lastAction,
    isTranslationPending,
    translationError,
    setIsProofOpen,
    closeModal,
    handleTranslateClick,
    handleProofreadClick,
    handleResetImproved,
    handleImprovedChange,
    handleApply,
  } = useAiWriterModal();

  return (
    <Modal
      title="Xometry Writer"
      open={modalVisible}
      onCancel={closeModal}
      className={s.modal}
      width={820}
      footer={
        <div className={s.footer}>
          {translationError ? <div className={s.errorText}>{translationError.message}</div> : null}
          <AntButton
            key="send"
            type="primary"
            onClick={handleApply}
            disabled={isTranslationPending}
            className={s.applyButton}
          >
            Apply
          </AntButton>
        </div>
      }
      destroyOnHidden
      centered
    >
      <Form form={form} layout="vertical">
        <Form.Item name={AiWriterModalFormFieldEnum.SourceMessage} label={<Label>Source message:</Label>}>
          <TextArea disabled={isTranslationPending} placeholder="Type the original message here…" rows={5} />
        </Form.Item>

        <Label>Improved message:</Label>
        <AntRow align="middle" wrap={false} className={s.improvedRow}>
          <AntCol flex="auto">
            <Form.Item name={AiWriterModalFormFieldEnum.Instructions} noStyle>
              <Input
                disabled={isTranslationPending}
                placeholder="Add instructions: Make it shorter, more polite, or more formal…"
              />
            </Form.Item>
          </AntCol>

          <AntCol>
            <Form.Item name={AiWriterModalFormFieldEnum.Locale} noStyle>
              <Select
                getPopupContainer={(trigger: HTMLElement) => trigger.parentElement as HTMLElement}
                disabled={isTranslationPending}
                className={s.localeSelect}
                placeholder="Select locale"
                options={localeOptions}
              />
            </Form.Item>
          </AntCol>

          <AntCol>
            <AntButton
              className={s.button}
              key="rewrite"
              onClick={handleTranslateClick}
              loading={isTranslationPending && lastAction === TranslateRequest.REWRITE}
            >
              {isImprovedEmpty ? 'Translate' : 'Rewrite'}
            </AntButton>
          </AntCol>

          <AntCol>
            <Tooltip title="Reset improved message">
              <AntButton
                className={s.button}
                disabled={isTranslationPending || !isUserEdited}
                key="reset"
                icon={<Reset />}
                onClick={handleResetImproved}
              />
            </Tooltip>
          </AntCol>
        </AntRow>

        <Form.Item name={AiWriterModalFormFieldEnum.ImprovedMessage}>
          <TextArea
            disabled={isTranslationPending}
            className={s.improvedTextarea}
            placeholder="The improved message will appear here..."
            rows={5}
            onChange={handleImprovedChange}
          />
        </Form.Item>

        <Collapse
          onChange={(keys) => {
            const open = Array.isArray(keys) ? keys.includes('proof-reading') : keys === 'proof-reading';
            setIsProofOpen(open);
          }}
        >
          <Collapse.Panel
            key="proof-reading"
            header={
              <div className={s.proofHeader}>
                <span className={s.proofTitle}>Proof-reading</span>
                <span className={s.proofSubtitle}>Check grammar or meaning in your language</span>
              </div>
            }
          >
            <AntRow gutter={8} align="middle" justify="end" wrap={false}>
              <AntCol>
                <Form.Item name={AiWriterModalFormFieldEnum.ProofReadingLocale} noStyle>
                  <Select
                    getPopupContainer={(trigger: HTMLElement) => trigger.parentElement as HTMLElement}
                    placeholder="Select locale"
                    options={localeOptions}
                  />
                </Form.Item>
              </AntCol>
              <AntCol>
                <Tooltip title="Translate to your language to verify the meaning">
                  <AntButton
                    className={s.button}
                    key="translate"
                    onClick={handleProofreadClick}
                    loading={isTranslationPending && lastAction === TranslateRequest.REFRESH_PROOF_READING}
                    disabled={isImprovedEmpty}
                  >
                    Proofread
                  </AntButton>
                </Tooltip>
              </AntCol>
            </AntRow>

            <Form.Item
              name={AiWriterModalFormFieldEnum.ProofReading}
              label={<Label>Proof-reading:</Label>}
              className={s.proofItem}
            >
              <TextArea disabled placeholder="The grammar-checked version will appear here" rows={5} />
            </Form.Item>
          </Collapse.Panel>
        </Collapse>
      </Form>
    </Modal>
  );
};
